/**
 * TryQuiPus — EncryptionService (AES-256-GCM)
 *
 * Cifra campos PII en el browser usando la Web Crypto API nativa.
 * La clave se deriva de la sesión Supabase via PBKDF2 — nunca se
 * almacena en localStorage ni se expone fuera del browser.
 *
 * Formato de ciphertext: `enc:${ivB64}:${ctB64}`
 * — el prefijo "enc:" distingue valores ya cifrados de legacy plaintext.
 *
 * Clave por empresa: derivada deterministamente de session.user.id + companyId
 * via PBKDF2-SHA-256 (100,000 iteraciones). Cache en memoria evita re-derivar.
 *
 * Regla de Oro #1: ninguna clave ni dato sale del browser.
 */

import { supabase } from './supabaseClient';

const ENC_SENTINEL = 'enc:';

// In-memory cache: companyId → CryptoKey (derived, non-extractable)
const _keyCache = new Map<string, CryptoKey>();

// ─── Key derivation ──────────────────────────────────────────────────

/**
 * Deriva una clave AES-256-GCM desde la sesión activa + companyId.
 * PBKDF2-SHA-256, 100,000 iteraciones — nunca se almacena en localStorage.
 */
export async function deriveKeyFromSession(companyId: string): Promise<CryptoKey> {
  if (_keyCache.has(companyId)) return _keyCache.get(companyId)!;

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.id) throw new Error('[EncryptionService] No active session');

  const encoder = new TextEncoder();
  const material = `${session.user.id}:${companyId}`;

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(material),
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  const key = await crypto.subtle.deriveKey(
    {
      name:       'PBKDF2',
      salt:       encoder.encode(companyId),
      iterations: 100_000,
      hash:       'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );

  _keyCache.set(companyId, key);
  return key;
}

/**
 * Verifica si existe una clave derivada en memoria para la empresa (sync).
 * Retorna true una vez que deriveKeyFromSession() se ha ejecutado al menos una vez.
 * Usado por la UI para mostrar badge "Datos cifrados".
 */
export function hasCompanyKey(companyId: string): boolean {
  return _keyCache.has(companyId);
}

// ─── Encrypt / Decrypt ────────────────────────────────────────────────

/**
 * Cifra un string con AES-256-GCM.
 * Retorna: `enc:${ivBase64}:${ciphertextBase64}`
 */
export async function encrypt(plaintext: string, key: CryptoKey): Promise<string> {
  const iv      = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV (GCM standard)
  const encoded = new TextEncoder().encode(plaintext);

  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);

  const ivB64 = btoa(String.fromCharCode(...iv));
  const ctB64 = btoa(Array.from(new Uint8Array(ct), (b) => String.fromCharCode(b)).join(''));

  return `${ENC_SENTINEL}${ivB64}:${ctB64}`;
}

/**
 * Descifra un string cifrado con `encrypt()`.
 * - Si `value` NO empieza con "enc:" lo retorna sin cambios (legacy passthrough).
 * - Si la clave no coincide (datos legacy cifrados con otra clave), retorna '[cifrado-legacy]'.
 */
export async function decrypt(value: string, key: CryptoKey): Promise<string> {
  if (!value.startsWith(ENC_SENTINEL)) return value; // passthrough

  const withoutSentinel = value.slice(ENC_SENTINEL.length);
  const colonIdx        = withoutSentinel.indexOf(':');

  if (colonIdx === -1) {
    console.warn('[EncryptionService] Malformed encrypted value; returning unchanged.');
    return value;
  }

  const ivB64 = withoutSentinel.slice(0, colonIdx);
  const ctB64 = withoutSentinel.slice(colonIdx + 1);

  const iv = Uint8Array.from(atob(ivB64), (c) => c.charCodeAt(0));
  const ct = Uint8Array.from(atob(ctB64), (c) => c.charCodeAt(0));

  try {
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
    return new TextDecoder().decode(decrypted);
  } catch {
    // Legacy data encrypted with a different key (e.g., old localStorage-based random key)
    console.warn('[EncryptionService] Decryption failed — legacy key mismatch.');
    return '[cifrado-legacy]';
  }
}
